import * as THREE from "three";
import potpack from "potpack";
import {sc} from "../../UI/Tools/VR/LHQ/Scene.js";
import {GetSingleColorTexture} from "./TextureUtils.js";
import {ForUsingUVsAsQuadVectors_FixWindingOrder} from "./UVsAsQuadVectors_WindingOrderFixer.js";
import {PRNG} from "../General/RNG/RNG.js";
import {RenderTargetToBase64} from "../../UI/@Shared/NavBar/TestingPanel/LightmapSettingsUI.js";
import {GetUVBounds} from "./General.js";
import {IsGlassMaterial} from "./LightMapUtils.js";

type Uniforms = {
	previousShadowMap: {value: THREE.Texture},
	time: {value: number},

	previousBounceMap: {value: THREE.Texture},
	stochasticDepthUVMap: {value: THREE.Texture},
	depthViewProjectionMatrix: {value: THREE.Matrix4},
	depthCameraX: {value: THREE.Vector3},
	depthCameraY: {value: THREE.Vector3},
	depthCameraDir: {value: THREE.Vector3},
	depthCameraPos: {value: THREE.Vector3},
	averagingWindow: {value: number},
	texelStride: {value: number},
	switchingNum: {value: number},
	numSamples: {value: number},

	indirectContribution: {value: number},
};
function GetUniforms(mat: THREE.Material | Array<THREE.Material>) {
	if (mat instanceof Array) throw new Error("Not implemented.");
	return (mat as THREE.Material)["uniforms"] as Uniforms;
}

/**
 * Progressive Light Map Accumulator, by [zalo](https://github.com/zalo/) [see also "Approximate Radiosity using Stochastic Depth Buffering" by Andreas Thomsen and Kasper Høy Nielsen]
 *
 * To use, simply construct a `ProgressiveLightMap` object,
 * `plmap.addObjectsToLightMap(object)` an array of semi-static
 * objects and lights to the class once, and then call
 * `plmap.update(camera)` every frame to begin accumulating
 * lighting samples.
 *
 * This should begin accumulating lightmaps which apply to
 * your objects, so you can start jittering lighting to achieve
 * the texture-space effect you're looking for.
 *
 * @param {WebGLRenderer} renderer A WebGL Rendering Context
 * @param {number} res The side-long dimension of you total lightmap */
export class ProgressiveLightMap {
	private renderer: THREE.WebGLRenderer;
	private camera: THREE.Camera;
	private res: number;
	private lightmapIntensity: number;
	private rng: PRNG;
	private getTime: ()=>number;

	private lightMapContainers: Array<{
		depthMat: THREE.Material;
		basicMat: THREE.Material;
		object: THREE.Object3D;
	}>;
	private scene: THREE.Scene;
	private tinyTarget: THREE.WebGLRenderTarget;
	private buffer1Active: boolean;
	/*private*/ firstUpdate: boolean;
	private warned: boolean;
	private depthMatrix: THREE.Matrix4;
	private cameraForward: THREE.Vector3;
	private lights: THREE.Light[];
	private numSamples: number;
	private switchingNum: number;
	private safari: boolean;
	private hasFragDepth: boolean;
	private mobile: boolean;
	/*private*/ progressiveLightMap1: THREE.WebGLRenderTarget;
	/*private*/ progressiveLightMap2: THREE.WebGLRenderTarget;
	private stochasticDepthColorBuffer: THREE.WebGLRenderTarget;
	private progressiveBounceMap1: THREE.WebGLRenderTarget;
	private progressiveBounceMap2: THREE.WebGLRenderTarget;
	/*private*/ compositeLightMap1: THREE.WebGLRenderTarget;
	/*private*/ compositeLightMap2: THREE.WebGLRenderTarget;
	private uvMat: THREE.Material;
	private uvMat_invertedNormals: THREE.Material;
	private bounceCamera: THREE.OrthographicCamera;
	private bounceGatherMaterial: THREE.Material;
	//private bounceGatherMaterial_invertedNormals: THREE.Material; // v-added
	private bounceGatherMaterial_invertUVs: THREE.Material; // v-added
	private compositeDirectAndIndirectMaterial: THREE.ShaderMaterial;
	private uv_boxes: Array<{
		w: number;
		h: number;
		index: number;
		surfaceAreaSqrt: number;
		x?: number;
		y?: number;

		/*uvBounds: {
			minU: number;
			maxU: number;
			minV: number;
			maxV: number;
			uvWidth: number;
			uvHeight: number;
		}*/
	}>;
	private blurringPlane: THREE.Mesh;
	/*private labelMaterial?: THREE.MeshBasicMaterial;
	private labelPlane?: THREE.PlaneGeometry;*/
	private labelMesh?: THREE.Mesh;
	private activeMap: THREE.WebGLRenderTarget;
	private inactiveMap: THREE.WebGLRenderTarget;

	constructor(renderer: THREE.WebGLRenderer, camera: THREE.Camera, res: number, lightmapIntensity: number, rng: PRNG, getTime: ()=>number) {
		this.renderer = renderer;
		this.camera = camera;
		this.res = res;
		this.lightmapIntensity = lightmapIntensity;
		this.rng = rng;
		this.getTime = getTime;

		this.lightMapContainers = [];
		this.scene = new THREE.Scene();
		this.scene.background = null;
		this.tinyTarget = new THREE.WebGLRenderTarget(1, 1);
		this.buffer1Active = false;
		this.firstUpdate = true;
		this.warned = false;
		this.depthMatrix = new THREE.Matrix4().identity();
		this.cameraForward = new THREE.Vector3();
		this.lights = [];
		this.numSamples = 1.0;
		this.switchingNum = 5000;
		this.safari = /(Safari)/g.test(navigator.userAgent) && ! /(Chrome)/g.test(navigator.userAgent);
		console.log("Renderer:", renderer);
		//console.log( "Safari: " + this.safari );
		//console.log( "WebGL2: " + renderer.capabilities.isWebGL2 );
		//console.log( "FragDepth: " + renderer.extensions.has( 'EXT_frag_depth' ) );
		this.hasFragDepth = renderer.capabilities.isWebGL2 || (renderer.extensions.has("EXT_frag_depth") && !this.safari);

		// Create the Progressive LightMap Textures
		this.mobile = /(Android|iPad|iPhone|iPod)/g.test(navigator.userAgent) || this.safari;
		const format = this.mobile ? THREE.HalfFloatType : THREE.FloatType;
		const curRes = this.res / (this.mobile ? 2.0 : 1.0), curLowRes = this.res / (this.mobile ? 4.0 : 2.0);

		this.progressiveLightMap1 = new THREE.WebGLRenderTarget(curRes, curRes, {type: format});
		this.progressiveLightMap2 = new THREE.WebGLRenderTarget(curRes, curRes, {type: format});
		this.stochasticDepthColorBuffer = new THREE.WebGLRenderTarget(curLowRes, curLowRes, {type: format, magFilter: THREE.NearestFilter, minFilter: THREE.NearestFilter});
		this.progressiveBounceMap1 = new THREE.WebGLRenderTarget(curLowRes, curLowRes, {type: format});
		this.progressiveBounceMap2 = new THREE.WebGLRenderTarget(curLowRes, curLowRes, {type: format});
		this.compositeLightMap1 = new THREE.WebGLRenderTarget(curRes, curRes, {type: format});
		this.compositeLightMap2 = new THREE.WebGLRenderTarget(curRes, curRes, {type: format});

		// v-added
		// this.progressiveLightMap1.texture.channel = 1;
		// this.progressiveLightMap2.texture.channel = 1;
		// this.stochasticDepthColorBuffer.texture.channel = 1;
		// this.progressiveBounceMap1.texture.channel = 1;
		// this.progressiveBounceMap2.texture.channel = 1;
		//this.compositeLightMap1.texture.channel = 1;
		this.progressiveLightMap1.texture.channel = 1;
		this.progressiveLightMap2.texture.channel = 1;
		this.compositeLightMap2.texture.channel = 1;

		// Inject some spicy new logic into a standard phong material
		this.uvMat = this._createUvMat(false);
		this.uvMat_invertedNormals = this._createUvMat(true);

		// This is Stochastic Order Independent Rendering for bouncing light off of layered surfaces
		this.bounceCamera = new THREE.OrthographicCamera(- 300 * sc, 300 * sc, 300 * sc, - 300 * sc, - 1000 * sc, 1000 * sc);
		this.scene.add(this.bounceCamera);

		this.bounceGatherMaterial = this._createBounceGatherMaterial(false); // v-changed
		//this.bounceGatherMaterial_invertedNormals = this._createBounceGatherMaterial(true); // v-added
		this.bounceGatherMaterial_invertUVs = this._createBounceGatherMaterial(true); // v-added

		this.compositeDirectAndIndirectMaterial = new THREE.ShaderMaterial({
			vertexShader: `
				#define USE_LIGHTMAP

				// v-added
				attribute vec2 uv1;
				#define LIGHTMAP_UV uv1

				// v-replaced
				/*attribute vec2 uv2;
				varying vec2 vUv2;
				void main() {
					vUv2 = uv2;
					gl_Position = vec4((uv2 - 0.5) * 2.0, 1.0, 1.0);
				}*/
				varying vec2 vLightMapUv;
				void main() {
					vLightMapUv = uv1;
					gl_Position = vec4((uv1 - 0.5) * 2.0, 1.0, 1.0);
				}
			`,
			fragmentShader: `
				// v-added
				#define USE_LIGHTMAP
				#define LIGHTMAP_UV uv1

				// v-replaced
				//varying vec2 vUv2; 
				varying vec2 vLightMapUv;

				uniform sampler2D previousShadowMap;
				uniform sampler2D previousBounceMap;
				uniform float indirectContribution;
				void main() {
					// v-replaced
					//gl_FragColor =   texture2D(previousShadowMap, vUv2) + (texture2D(previousBounceMap, vUv2) * indirectContribution);
					gl_FragColor =   texture2D(previousShadowMap, vLightMapUv) + (texture2D(previousBounceMap, vLightMapUv) * indirectContribution);
				}
			`,
			uniforms: {
				previousShadowMap: {value: this.progressiveLightMap2.texture},
				previousBounceMap: {value: this.progressiveBounceMap2.texture}
			}
		});
		this.compositeDirectAndIndirectMaterial.name = "compositeDirectAndIndirectMaterial";

	}

	/** Adds these objects to the lightmapping engine.
	 * @param {Object3D} objects An array of objects and lights to set up your lightmap.*/
	addObjectsToLightMap(objects: Array<THREE.Object3D>) {
		// v-added
		/*objects = objects.filter( ( obj )=>{
			if (obj["addedToLightmap"]) return false;
			obj["addedToLightmap"] = true;
			return true;
		});*/

		// Prepare list of UV bounding boxes for packing later...

		// v-changed; 0.01 and lower have "bleed" of light to unrelated surfaces (well actually 0.02 and even 0.03 can have bleed, but only after a really long time; can easily lock lightmap updates before then)		
		this.uv_boxes = []; const padding = (3 / this.res).KeepAtLeast(.02);

		for (let ob = 0; ob < objects.length; ob++) {
			const object = objects[ob] as THREE.Object3D;

			//if (ob == objects.length - 1) {
			//	let helper = new THREE.CameraHelper( this.bounceCamera );
			//	object.parent.add( helper );
			//}

			// If this object is a light, simply add it to the internal scene
			if (object["isLight"]) {
				object["shadow"].autoUpdate = false;
				this.lights.push(object as any);
				this.scene.attach(object);
				continue;
			}

			if (!(object["geometry"] as THREE.BufferGeometry).hasAttribute("uv")) {
				console.warn(`All lightmap objects need UVs! Missing for: "${object.name}"`);
				continue;
			}

			if (this.blurringPlane == null) {
				this._initializeBlurPlane(this.res, this.progressiveLightMap1);
			}

			const uniforms = {
				previousShadowMap: {value: this.progressiveLightMap1.texture},
				time: {value: this.getTime()},
				depthCameraDir: {value: this.cameraForward.setFromMatrixColumn(this.bounceCamera.matrixWorld, 2).clone()}
			};

			// Inject some spicy stochastic depth logic into this object's material

			// v-replaced; ThreeJS MeshBasicMaterial apparently used to support the modified behavior below, but in latest versions (0.174.0), it apparently doesn't; so standardize on always using MeshPhongMaterial for this stochasticDepthMaterial
			//let stochasticDepthMaterial = object["material"].clone();
			const stochasticDepthMaterial = object["material"] instanceof THREE.MeshPhongMaterial
				? object["material"].clone()
				: new THREE.MeshPhongMaterial({color: object["material"].color});
				//: new THREE.MeshPhongMaterial( { color: new THREE.Color("rgb(255,255,255)"), depthWrite: true } );
			const colorBoost = object["material"]["colorBoostForLightmap"] ?? 1;
			if (colorBoost != 1) {
				stochasticDepthMaterial.color.r *= colorBoost;
				stochasticDepthMaterial.color.g *= colorBoost;
				stochasticDepthMaterial.color.b *= colorBoost;
			}

			stochasticDepthMaterial.name = object["material"].name + "_stochasticDepthMaterial";
			stochasticDepthMaterial["uniforms"] = {};
			stochasticDepthMaterial["extensions"] = {
				// v-replaced
				//fragDepth: this["fragDepth"]
				fragDepth: this.hasFragDepth
			}; // set to use fragment depth values
			stochasticDepthMaterial.onBeforeCompile = shader=>{
				// Vertex Shader: Set Vertex Positions to the Unwrapped UV Positions
				shader.vertexShader =
					`
					#define USE_LIGHTMAP

					// v-added
					attribute vec2 uv1;
					#define LIGHTMAP_UV uv1

					//# define USE_SHADOWMAP
					varying float depth;
					uniform vec3 depthCameraDir;
					//varying vec2 vUv2;
					` +
					shader.vertexShader.slice(0, - 1) +
					`	//vUv2 = uv2;
						depth = dot(worldPosition.xyz, depthCameraDir); }`;
				// Fragment Shader: Set Pixels to average in the Previous Frame's Shadows
				const bodyStart = shader.fragmentShader.indexOf("void main() {");
				shader.fragmentShader =

					// v-added; we must add this very early on! (the pre-body-start insert point below is too late!)
					`
						#define USE_LIGHTMAP
						#define LIGHTMAP_UV uv1
						` +


					shader.fragmentShader.slice(0, bodyStart) +
					`	
						uniform sampler2D previousShadowMap;
						varying float depth;
						uniform float time;
						//varying vec2 vUv2; // v-removed
						float randd(vec2 co){ // Author @patriciogv - 2015; http://patriciogonzalezvivo.com
							return fract(sin(dot(co.xy, vec2(12.9898,78.233) + time)) * 43758.5453);
						}
						` +
					shader.fragmentShader.slice(bodyStart - 1, - 1) +
					`gl_FragColor.a = depth; // Extra value here, use for opacity?` +

					// v-replaced
					//( this.hasFragDepth ? 'gl_FragDepthEXT = (randd(vUv2) * 20.0)-10.0;\n' : '\n' ) + '}';
					(this.hasFragDepth ? "gl_FragDepthEXT = (randd(vLightMapUv) * 20.0)-10.0;\n" : "\n") + "}";

				Object.assign(shader.uniforms, uniforms);
				stochasticDepthMaterial["uniforms"] = shader.uniforms;
				stochasticDepthMaterial.userData.shader = shader;
			};

			// Apply the lightmap to the object
			object["material"].dithering = true;
			//object.castShadow = true;
			object.receiveShadow = true;
			object.renderOrder = 1000 + ob;

			// Prepare UV boxes for potpack
			if (!object["excludeFromLightMap"]) {
				const uvsForLightmap = object["uvsForLightmap"] ?? object["geometry"].getAttribute("uv");
				// Determine the true surface area of this mesh as a ratio of world surface area to used UV surface area
				const surfaceAreaSqrt = Math.sqrt(this._findSurfaceArea(object, false) / this._findSurfaceArea(object, true, uvsForLightmap));

				//if (object["partOfFloor"]) console.log("Part of floor:", object.name, "Surface Area Sqrt:", surfaceAreaSqrt); // for debugging

				this.uv_boxes.push({
					w: surfaceAreaSqrt * (1 + (padding * 2)),
					h: surfaceAreaSqrt * (1 + (padding * 2)),
					index: ob, surfaceAreaSqrt: surfaceAreaSqrt
				});

				// glass materials/slots get no lightmap (else the panes become dark; lightmaps are meant for the frames only)
				const mats = (Array.isArray(object["material"]) ? object["material"] : [object["material"]]) as THREE.MeshStandardMaterial[];
				for (const mat of mats) {
					if (IsGlassMaterial(mat)) continue;
					mat.lightMap = this.progressiveLightMap2.texture; // 2025-04-02: this also works the same atm (since not using indirect-lighting atm)
					//mat.lightMap = this.compositeLightMap2.texture;
					//mat.lightMap = GetSingleColorTexture("rgb(255,255,255)");
					mat.lightMapIntensity = this.lightmapIntensity;
				}
			}

			this.lightMapContainers.push({
				depthMat: stochasticDepthMaterial,
				basicMat: object["material"],
				object: object
			});
		}

		// Pack the objects' lightmap UVs into the same global space
		const dimensions = potpack(this.uv_boxes);
		console.log("Dimensions:", JSON.stringify(dimensions));
		this.uv_boxes.forEach(box=>{
			const obj = objects[box.index] as THREE.Mesh; // non-meshes (ie. lights) are skipped in the earlier loop
			//const uv1 = (obj["uv_preScaling"] ?? obj.geometry.getAttribute("uv")).clone();
			//const uv1 = obj.geometry.getAttribute("uv").clone();
			const uv1 = (obj["uvsForLightmap"] ?? obj.geometry.getAttribute("uv")).clone();

			ForUsingUVsAsQuadVectors_FixWindingOrder(uv1, obj); // v-added

			for (let i = 0; i < uv1.array.length; i += uv1.itemSize) {
				uv1.array[i] = (((uv1.array[i] * box.surfaceAreaSqrt)) + box.x! + padding) / dimensions.w;
				uv1.array[i + 1] = ((uv1.array[i + 1] * box.surfaceAreaSqrt) + box.y! + padding) / dimensions.h;
			}

			obj.geometry.setAttribute("uv1", uv1);
			obj.geometry.getAttribute("uv1").needsUpdate = true;
		});
	}

	resetLightmap(camera: THREE.Camera) {
		this.buffer1Active = false;
		this.updatesSinceReset = 0;
		this.update(camera, 1, false, 0.000001, 1);
	}

	updatesSinceReset = 0;
	/** This function renders each mesh one at a time into the global lightmap.
	 * @param {Camera} camera Standard Rendering Camera
	 * @param {number} blendWindow When >1, samples will accumulate over time.
	 * @param {boolean} blurEdges  Whether to fix UV Edges via blurring */
	update(camera: THREE.Camera, blendWindow: number, blurEdges: boolean, indirectContribution: number, indirectBlendWindow: number) {
		// Return if there are no objects in the lightmap
		if (this.lightMapContainers.length === 0) return;
		this.updatesSinceReset++;

		// Store the original Render Target
		const oldTarget = this.renderer.getRenderTarget();

		//let last = performance.now();

		// Configure lightmapping-specific rendering
		this._prepareObjectsForLighting(camera, blurEdges);

		//console.log("1: ", performance.now() - last); last = performance.now();

		// RenderPass accumulating 8 Directional Lights at once
		this._renderDirectLighting(camera, blendWindow);

		//console.log("2: ", performance.now() - last); last = performance.now();

		// RenderPass accumulating 1 or 7 Indirect Lighting passes
		if (indirectContribution > 0) {
			for (let bounceIter = 0; bounceIter < 1 + (this.mobile ? 0 : 6); bounceIter++) {
				this.buffer1Active = !this.buffer1Active;
				this._renderIndirectLighting(indirectBlendWindow);
			}
		} else {
			this.buffer1Active = !this.buffer1Active;
		}

		//console.log("3: ", performance.now() - last); last = performance.now();

		// Combine the results of the Direct and Indirect Passes
		this._compositeDirectAndIndirectLighting(camera, indirectContribution, blurEdges);

		//console.log("4: ", performance.now() - last); last = performance.now();

		// Restore the original settings
		this._resetObjectLightingState();
		this.renderer.setRenderTarget(oldTarget);

		//console.log("5: ", performance.now() - last); last = performance.now();
	}

	// END PUBLIC API

	_prepareObjectsForLighting(camera, blurEdges) {
		// The blurring plane applies blur to the seams of the lightmap
		this.blurringPlane.visible = blurEdges;

		// Manually set all of the lights to update their shadows
		for (let l = 0; l < this.lights.length; l++) {
			this.lights[l].shadow!.needsUpdate = true;
		}

		// Steal the Object3D from the real world to our special dimension
		for (let l = 0; l < this.lightMapContainers.length; l++) {
			this.lightMapContainers[l].object["oldScene"] = this.lightMapContainers[l].object.parent;
			this.scene.attach(this.lightMapContainers[l].object);
		}

		// Render once normally to initialize everything
		// This is necessary; I'm not sure exactly why...
		if (this.firstUpdate) {
			// v-replaced
			/*this.renderer.setRenderTarget( this.tinyTarget ); // Tiny for Speed
			this.renderer.render( this.scene, camera );*/
			this.renderer.compile(this.scene, camera);

			this.firstUpdate = false;
		}
	}

	/** A pass that samples and accumulates a set of lights. */
	_renderDirectLighting(camera, blendWindow: number) {
		const resetFuncs = [] as (()=>any)[];

		//blendWindow = 2; // temp

		// Set each object's material to the UV Unwrapped Surface Mapping Version
		for (let l = 0; l < this.lightMapContainers.length; l++) {
			GetUniforms(this.uvMat).averagingWindow = {value: blendWindow};
			GetUniforms(this.uvMat_invertedNormals).averagingWindow = {value: blendWindow};
			const obj = this.lightMapContainers[l].object;
			//console.log("Found:", obj, "InvertNormalsForDirect:", obj["invertNormalsForDirectLighting"]);

			// v-replaced
			//obj["material"] = this.uvMat;
			obj["material"] = obj["invertNormalsForDirectLighting_shaderOnly"] ? this.uvMat_invertedNormals : this.uvMat;
			/*if (obj["invertWindingOrderForDirectLighting"]) {
				obj["geometry"].v_index_orig ??= obj["geometry"].index.array.map(a=>a);
				obj["geometry"].v_index_flipped ??= obj["geometry"].index.array.map(a=>-a);

				//obj["geometry"].setIndex(obj["geometry"].v_index_flipped);
				obj["geometry"].index.array = obj["geometry"].v_index_flipped;
				obj["geometry"].index.needsUpdate = true;
				resetFuncs.push(()=>{
					//obj["geometry"].setIndex(obj["geometry"].v_index_orig)
					obj["geometry"].index.array = obj["geometry"].v_index_orig;
					obj["geometry"].index.needsUpdate = true;
				});
			}*/
			if (obj["invertNormalsForDirectLighting"]) {
				obj["geometry"].v_normals_orig ??= obj["geometry"].attributes.normal.array.map(a=>a);
				obj["geometry"].v_normals_flipped ??= obj["geometry"].attributes.normal.array.map(a=>-a);

				obj["geometry"].attributes.normal.array = obj["geometry"].v_normals_flipped;
				obj["geometry"].attributes.normal.needsUpdate = true;
				resetFuncs.push(()=>{
					obj["geometry"].attributes.normal.array = obj["geometry"].v_normals_orig
					obj["geometry"].attributes.normal.needsUpdate = true;
				});
			}

			obj["oldFrustumCulled"] = obj.frustumCulled;
			obj.frustumCulled = false;
			obj.visible = !obj["excludeFromLightMap"];

			// v-added
			if (obj["excludeFromDirectLight"]) obj.visible = false;
		}

		// Ping-pong two surface buffers for reading/writing
		this.activeMap = this.buffer1Active ? this.progressiveLightMap1 : this.progressiveLightMap2;
		this.inactiveMap = this.buffer1Active ? this.progressiveLightMap2 : this.progressiveLightMap1;
		//let activeBounceMap = this.buffer1Active ? this.progressiveBounceMap1 : this.progressiveBounceMap2;
		const inactiveBounceMap = this.buffer1Active ? this.progressiveBounceMap2 : this.progressiveBounceMap1;

		// Render the object's surface maps
		this.renderer.setRenderTarget(this.activeMap);
		GetUniforms(this.uvMat).previousShadowMap = {value: this.inactiveMap.texture};
		GetUniforms(this.uvMat_invertedNormals).previousShadowMap = {value: this.inactiveMap.texture};
		GetUniforms(this.uvMat).previousBounceMap = {value: inactiveBounceMap.texture};
		GetUniforms(this.uvMat_invertedNormals).previousBounceMap = {value: inactiveBounceMap.texture};
		GetUniforms(this.blurringPlane.material).previousShadowMap = {value: this.inactiveMap.texture};

		this.renderer.render(this.scene, camera);

		resetFuncs.forEach(a=>a()); // v-added
	}

	/** A pass that stochastically samples global bounce lighting along a single direction. */
	_renderIndirectLighting(indirectBlendWindow) {
		this.blurringPlane.visible = false;

		// Set each object's material to the stochastic depth version
		for (const obj of this.lightMapContainers) {
			GetUniforms(obj.depthMat).time = {value: this.getTime()};
			GetUniforms(obj.depthMat).depthCameraDir = {value: this.cameraForward.setFromMatrixColumn(this.bounceCamera.matrixWorld, 2).clone()};
			obj.depthMat.needsUpdate = true;
			obj.object["material"] = obj.depthMat;
			obj.object.visible = true;
		}

		// Render the stochastic depth buffer from a random directional camera's perspective
		this.renderer.setRenderTarget(this.stochasticDepthColorBuffer);
		this.renderer.render(this.scene, this.bounceCamera);

		// Update the depth camera matrix
		this.depthMatrix.set(
			0.5, 0.0, 0.0, 0.5,
			0.0, 0.5, 0.0, 0.5,
			0.0, 0.0, 0.5, 0.5,
			0.0, 0.0, 0.0, 1.0);
		this.depthMatrix.multiply(this.bounceCamera.projectionMatrix);
		this.depthMatrix.multiply(this.bounceCamera.matrixWorldInverse);

		// Ping-pong two surface buffers for reading/writing
		const activeBounceMap = this.buffer1Active ? this.progressiveBounceMap1 : this.progressiveBounceMap2;
		const inactiveBounceMap = this.buffer1Active ? this.progressiveBounceMap2 : this.progressiveBounceMap1;

		// v-changed
		for (const bounceGatherMat of [this.bounceGatherMaterial, this.bounceGatherMaterial_invertUVs]) {
			const bounceGatherMat_uniforms = bounceGatherMat["uniforms"] as Uniforms;
			bounceGatherMat_uniforms.previousShadowMap = {value: this.inactiveMap.texture};
			bounceGatherMat_uniforms.previousBounceMap = {value: inactiveBounceMap.texture};
			bounceGatherMat_uniforms.stochasticDepthUVMap = {value: this.stochasticDepthColorBuffer.texture};
			bounceGatherMat_uniforms.depthViewProjectionMatrix = {value: this.depthMatrix};
			bounceGatherMat_uniforms.depthCameraX = {value: this.cameraForward.setFromMatrixColumn(this.bounceCamera.matrixWorld, 0).normalize().clone()};
			bounceGatherMat_uniforms.depthCameraY = {value: this.cameraForward.setFromMatrixColumn(this.bounceCamera.matrixWorld, 1).normalize().clone()};
			bounceGatherMat_uniforms.depthCameraDir = {value: this.cameraForward.setFromMatrixColumn(this.bounceCamera.matrixWorld, 2).clone()};
			bounceGatherMat_uniforms.depthCameraPos = {value: new THREE.Vector3()};// "Pos" is whatever the center of the frustum is//this.bounceCamera.position };
			bounceGatherMat_uniforms.averagingWindow = {value: indirectBlendWindow};
			bounceGatherMat_uniforms.texelStride = {value: 1.0 / this.res};
			bounceGatherMat_uniforms.switchingNum = {value: indirectBlendWindow};
			bounceGatherMat_uniforms.numSamples = {value: this.numSamples};
			bounceGatherMat.needsUpdate = true;
		}
		this.blurringPlane.visible = true;
		((this.blurringPlane.material as THREE.Material)["uniforms"] as Uniforms).previousShadowMap = {value: inactiveBounceMap.texture};

		const resetFuncs = [] as (()=>any)[];

		// Set each object's material to the bounce gathering material
		for (let l = 0; l < this.lightMapContainers.length; l++) {
			const obj = this.lightMapContainers[l].object;
			//console.log("For:", obj.name, "Invert:", obj["invertNormalsForLightmap"]);

			// v-replaced
			//obj["material"] = this.bounceGatherMaterial;
			//obj["material"] = obj["invertNormalsForLightmap_shaderOnly"] ? this.bounceGatherMaterial_invertedNormals : this.bounceGatherMaterial;
			obj["material"] = obj["invertUVsForLightmap_shaderOnly"] ? this.bounceGatherMaterial_invertUVs : this.bounceGatherMaterial;
			if (obj["invertNormalsForLightmap"]) {
				obj["geometry"].v_normals_orig ??= obj["geometry"].attributes.normal.array.map(a=>a);
				obj["geometry"].v_normals_flipped ??= obj["geometry"].attributes.normal.array.map(a=>-a);

				obj["geometry"].attributes.normal.array = obj["geometry"].v_normals_flipped;
				obj["geometry"].attributes.normal.needsUpdate = true;
				resetFuncs.push(()=>{
					obj["geometry"].attributes.normal.array = obj["geometry"].v_normals_orig
					obj["geometry"].attributes.normal.needsUpdate = true;
				});
			}

			obj.visible = !obj["excludeFromLightMap"];
		}

		// Uniform Hemispherical Surface Distribution to sample all ray directions equally
		const lambda = Math.acos(2 * this.rng.GetNextFloat() - 1) - (3.14159 / 2.0);
		const phi = 2 * 3.14159 * this.rng.GetNextFloat();
		this.bounceCamera.position.set(((Math.cos(lambda) * Math.cos(phi)) * 300 * sc),
			((Math.cos(lambda) * Math.sin(phi)) * 300 * sc) + 20 * sc,
			(Math.sin(lambda) * 300 * sc));
		//this.bounceCamera.position.set( 300, 300, 300 );
		this.bounceCamera.lookAt(0, 0, 0);
		this.bounceCamera.updateProjectionMatrix();

		// Gather the bounce
		this.renderer.setRenderTarget(activeBounceMap);
		this.renderer.render(this.scene, this.bounceCamera);

		this.numSamples = Math.min(indirectBlendWindow, this.numSamples + 1.0);

		resetFuncs.forEach(a=>a()); // v-added
	}

	/** A pass that combines the direct and indirect passes into a single lightmap */
	_compositeDirectAndIndirectLighting(camera, indirectContribution, blurEdges) {
		const activeCompositeMap = this.buffer1Active ? this.compositeLightMap1 : this.compositeLightMap2;
		const inactiveCompositeMap = this.buffer1Active ? this.compositeLightMap2 : this.compositeLightMap1;
		this.blurringPlane.visible = blurEdges;
		GetUniforms(this.blurringPlane.material).previousShadowMap = {value: inactiveCompositeMap.texture};
		this.compositeDirectAndIndirectMaterial.uniforms.previousShadowMap = {value: this.progressiveLightMap1.texture};
		this.compositeDirectAndIndirectMaterial.uniforms.previousBounceMap = {value: this.progressiveBounceMap1.texture};
		this.compositeDirectAndIndirectMaterial.uniforms.indirectContribution = {value: indirectContribution / (this.numSamples)};

		// Composite the direct and Indirect Lighting Steps together
		for (let l = 0; l < this.lightMapContainers.length; l++) {
			const obj = this.lightMapContainers[l].object;
			obj["material"] = this.compositeDirectAndIndirectMaterial;
			obj.visible = !obj["excludeFromLightMap"];
		}

		// Gather the bounce
		this.renderer.setRenderTarget(activeCompositeMap);
		this.renderer.render(this.scene, camera);
	}

	/** Restore the objects' original material and add them back to the original scene */
	_resetObjectLightingState() {
		for (let l = 0; l < this.lightMapContainers.length; l++) {
			const obj = this.lightMapContainers[l].object;
			obj.frustumCulled = obj["oldFrustumCulled"];

			obj["material"] = this.lightMapContainers[l].basicMat;

			obj["oldScene"].attach(obj);
			obj.visible = obj["visibleOutsideLightmap"] ?? true; // v-changed
		}
	}

	_createUvMat(invertedNormals: boolean) {
		const result = this._createModifiedMaterial(new THREE.MeshPhongMaterial(),
			{
				previousShadowMap: {value: this.progressiveLightMap1.texture},
				previousBounceMap: {value: this.progressiveBounceMap1.texture},
				averagingWindow: {value: 100}, // don't worry about this value; it gets overridden in this._renderDirectLighting() (before any render that uses it) anyway
			},

			// vertex-shader parts
			`
				#define USE_LIGHTMAP

				// v-added
				attribute vec2 uv1;
				#define LIGHTMAP_UV uv1
				
			`,
			"",
			`
				// v-replaced
				//gl_Position = vec4((uv2 - 0.5) * 2.0, 1.0, 1.0); }
				gl_Position = vec4((LIGHTMAP_UV - 0.5) * 2.0, 1.0, 1.0); }
			`,

			// fragment-shader parts
			`
				// v-removed
				//varying vec2 vUv2;

				#define USE_LIGHTMAP
				
				// v-added
				//attribute vec2 uv1;
				//#define LIGHTMAP_UV uv1
			`,
			`	uniform sampler2D previousShadowMap;
				uniform sampler2D previousBounceMap;
				uniform float averagingWindow;
			`,
			`
				// v-replaced
				//vec3 texelOld = texture2D(previousShadowMap, vUv2).rgb;
				vec3 texelOld = texture2D(previousShadowMap, vLightMapUv).rgb;

				gl_FragColor.rgb = mix(texelOld, gl_FragColor.rgb, 1.0/averagingWindow);

				//gl_FragColor.rgb = vec3(vLightMapUv.x, vLightMapUv.y, 0.0);
				
				//gl_FragColor.rgb = vec3(1.0, 1.0, 1.0);
			}`,

			// v-added; for some reason we need to flip the normal for direct lighting, for some objects
			undefined,
			undefined,
			shader=>{
				if (invertedNormals) shader.flipSided = true;
				//console.log("UVMat_shader_vertex:", shader.vertexShader);
				//console.log("UVMat_shader_frag:", shader.fragmentShader);
			},
		);
		result.name = `uvMat${invertedNormals ? "_invertedNormals" : ""}`;
		//result.side = THREE.DoubleSide; // FIX FOR ENRAGING ISSUE?
		return result;
	}

	/** INTERNAL: Create the material that gathers samples from the stochastic depth. 
	 * @returns {THREE.Material} */
	_createBounceGatherMaterial(
		//invertedNormals: boolean,
		invertUVs: boolean
	) {
		const result = new THREE.MeshPhongMaterial();
		//result.side = THREE.DoubleSide; // FIX FOR ENRAGING ISSUE?
		//result.name = `bounceGatherMaterial${invertedNormals ? "_invertedNormals" : ""}`;
		result.name = `bounceGatherMaterial${invertUVs ? "_invertUVs" : ""}`;
		result["uniforms"] = {} as any;
		result.onBeforeCompile = shader=>{
			//console.log("MeshPhongMaterial_BaseVertexShaderCode:", shader.vertexShader);
			//console.log("MeshPhongMaterial_BaseFragmentShaderCode:", shader.fragmentShader);
			// Vertex Shader: Calculate Depth and Set Vertex Positions to the Unwrapped UV Positions
			shader.vertexShader =
				`
				#define USE_LIGHTMAP

				// v-added
				#define DISTANCE // needed because otherwise the worldPosition variable is not calculated (see: https://github.com/mrdoob/three.js/blob/f636bf1f7b0d177ea66504800b170ea73872c0e7/src/renderers/shaders/ShaderChunk/worldpos_vertex.glsl.js#L2)
				attribute vec2 uv1;
				#define LIGHTMAP_UV uv1

				varying vec4 vWorldPosition;
				` +
				shader.vertexShader.slice(0, - 1) +
				`	vWorldPosition = worldPosition;

					// v-replaced
					//gl_Position = vec4((uv2 - 0.5) * 2.0, 1.0, 1.0);
					//gl_Position = vec4((uv1 - 0.5) * 2.0, 1.0, 1.0);

					//gl_Position = vec4((vec2(1.0) - uv1 - 0.5) * 2.0, 1.0, 1.0);

					${invertUVs
					? "gl_Position = vec4((vec2(1.0) - uv1 - 0.5) * 2.0, 1.0, 1.0);"
					: "gl_Position = vec4((uv1 - 0.5) * 2.0, 1.0, 1.0);"
				}
				}
				`;

			// Fragment Shader: Gather the correct appropriate bounce surface illumination
			// Then Set Pixels to average in the Previous Frame's Shadows
			const bodyStart = shader.fragmentShader.indexOf("void main() {");
			shader.fragmentShader =
				`
				// v-added
				#define USE_LIGHTMAP
				#define LIGHTMAP_UV uv1
				
				//varying vec2 vUv2; // v-removed
				
				varying vec4 vWorldPosition;
				` +
				shader.fragmentShader.slice(0, bodyStart) +
				`	uniform sampler2D previousShadowMap;
					uniform sampler2D previousBounceMap;
					uniform sampler2D stochasticDepthUVMap;
					uniform mat4 depthViewProjectionMatrix;
					uniform vec3 depthCameraX;
					uniform vec3 depthCameraY;
					uniform vec3 depthCameraDir;
					uniform vec3 depthCameraPos;
					uniform float texelStride;
					uniform float switchingNum;
					uniform float numSamples;
					uniform vec2 sampleOffset[9];
					vec3 GetWorldPos(vec2 illumTexCoord, float depth) {
						return (depthCameraDir * depth) +
								// note: at first I thought the precision changed the outcome, but I think it's actually just variance based on how quickly screenshot was taken relative to rendering-start
							   //((600.0 * ${sc.toFixed(2)} * illumTexCoord.x - 300.0 * ${sc.toFixed(2)}) * depthCameraX) +
							   //((600.0 * ${sc.toFixed(2)} * illumTexCoord.y - 300.0 * ${sc.toFixed(2)}) * depthCameraY);
								// pre-calculating const*sc, for better perf
							   ((${(600 * sc).toFixed(2)} * illumTexCoord.x - ${(300 * sc).toFixed(2)}) * depthCameraX) +
							   ((${(600 * sc).toFixed(2)} * illumTexCoord.y - ${(300 * sc).toFixed(2)}) * depthCameraY);
					}
					// From "Approximate Radiosity using Stochastic Depth Buffering" by Andreas Thomsen and Kasper Høy Nielsen
					vec3 CalculateIndirectLight(vec4 vWorldPosition, vec3 normal) {
						float weight = dot(normal, depthCameraDir);
						if (weight >= 0.0) return vec3(0,0,0);
						//if (weight >= 0.0) weight = -weight; 
						
						vec4 bestSample = vec4(0, 0.0, 0.0, -100000.0); //SkyColorInDirection(d)
						vec2 illumPos = (depthViewProjectionMatrix * vWorldPosition).xy;
						vec2 illumTexCoord = vec2(0,0);
						vec4 illumSample = vec4(0,0,0,0);//texture2D(stochasticDepthUVMap, illumPos);
						vec3 samplePos = vec3(0,0,0);
						#pragma unroll_loop_start
						for (int i = 0; i < 9; i++) {
							illumTexCoord = illumPos + (sampleOffset[i] * texelStride);
							illumSample = texture2D(stochasticDepthUVMap, illumTexCoord);
							samplePos = GetWorldPos(illumTexCoord, illumSample.w);
							if (dot(samplePos-vWorldPosition.xyz, normal) > 0.0 && illumSample.w > bestSample.w) {
								bestSample = illumSample;
							}
						}
						#pragma unroll_loop_end

						/*bestSample.r = min(1.0, bestSample.r);
						bestSample.g = min(1.0, bestSample.g);
						bestSample.b = min(1.0, bestSample.b);*/

						return 4.0 * bestSample.rgb * -weight;
					}
					` +
				shader.fragmentShader.slice(bodyStart - 1, - 1) +
				`
					// v-replaced
					//vec3 worldNormal = inverseTransformDirection( normal, viewMatrix );
					vec3 worldNormal = inverseTransformDirection( ${/*(invertedNormals || false) ? "-" :*/ ""}normal, viewMatrix );

					gl_FragColor.rgb = CalculateIndirectLight(vWorldPosition, worldNormal);

					// v-replaced
					//vec3 texelOld = texture2D(previousBounceMap, vUv2).rgb;
					vec3 texelOld = texture2D(previousBounceMap, vLightMapUv).rgb;

					gl_FragColor.rgb += texelOld * ((numSamples >= switchingNum) ? (switchingNum-1.0)/switchingNum : 1.0);
				}`;

			// Set the Previous Frame's Texture Buffer and Averaging Window
			shader.uniforms.previousShadowMap = {value: this.progressiveLightMap2.texture};
			shader.uniforms.previousBounceMap = {value: this.progressiveBounceMap2.texture};
			shader.uniforms.stochasticDepthUVMap = {value: this.stochasticDepthColorBuffer.texture};
			shader.uniforms.depthViewProjectionMatrix = {value: this.depthMatrix};
			shader.uniforms.depthCameraX = {value: this.cameraForward.setFromMatrixColumn(this.bounceCamera.matrixWorld, 0).multiplyScalar(- 1.0).clone()};
			shader.uniforms.depthCameraY = {value: this.cameraForward.setFromMatrixColumn(this.bounceCamera.matrixWorld, 1).multiplyScalar(- 1.0).clone()};
			shader.uniforms.depthCameraDir = {value: this.cameraForward.setFromMatrixColumn(this.bounceCamera.matrixWorld, 2).clone()};
			shader.uniforms.depthCameraPos = {value: this.bounceCamera.position};
			shader.uniforms.switchingNum = {value: this.switchingNum};
			shader.uniforms.numSamples = {value: this.numSamples};
			shader.uniforms.texelStride = {value: 1.0 / this.res};
			shader.uniforms.sampleOffset = {
				value: [new THREE.Vector2(- 1, - 1),
					new THREE.Vector2(0, - 1),
					new THREE.Vector2(1, - 1),
					new THREE.Vector2(- 1, 0),
					new THREE.Vector2(0, 0),
					new THREE.Vector2(1, 0),
					new THREE.Vector2(- 1, 1),
					new THREE.Vector2(0, 1),
					new THREE.Vector2(1, 1)]
			};


			result["uniforms"] = shader.uniforms as any;

			// Set the new Shader to this
			result.userData.shader = shader;

			// v-added
			/*if (invertedNormals) {
				shader.flipSided = true;
			}*/
		};
		return result;
	}

	/** INTERNAL Creates the Blurring Plane
	 * @param {number} res The square resolution of this object's lightMap.
	 * @param {WebGLRenderTarget} lightMap The lightmap to initialize the plane with. */
	_initializeBlurPlane(res: number, lightMap: THREE.WebGLRenderTarget | n = null) {
		const uniforms = {
			previousShadowMap: {value: lightMap!.texture},
			pixelOffset: {value: 1.0 / res},
			polygonOffset: true, polygonOffsetFactor: - 1, polygonOffsetUnits: 3.0
		};

		const blurMaterial = this._createModifiedMaterial(new THREE.MeshBasicMaterial(), uniforms as any,
			"#define USE_UV",
			"",
			"	gl_Position = vec4((uv - 0.5) * 2.0, 1.0, 1.0); }",

			"#define USE_UV",
			"	uniform sampler2D previousShadowMap;\n	uniform float pixelOffset;\n",
			`	gl_FragColor.rgb = (
					texture2D(previousShadowMap, vUv + vec2( pixelOffset,  0.0        )).rgb + 
					texture2D(previousShadowMap, vUv + vec2( 0.0        ,  pixelOffset)).rgb +
					texture2D(previousShadowMap, vUv + vec2( 0.0        , -pixelOffset)).rgb +
					texture2D(previousShadowMap, vUv + vec2(-pixelOffset,  0.0        )).rgb +
					texture2D(previousShadowMap, vUv + vec2( pixelOffset,  pixelOffset)).rgb + 
					texture2D(previousShadowMap, vUv + vec2(-pixelOffset,  pixelOffset)).rgb +
					texture2D(previousShadowMap, vUv + vec2( pixelOffset, -pixelOffset)).rgb +
					texture2D(previousShadowMap, vUv + vec2(-pixelOffset, -pixelOffset)).rgb
				) / 8.0;
			}`
		);
		blurMaterial.name = "blurMaterial";

		//blurMaterial.side = THREE.DoubleSide; // FIX FOR ENRAGING ISSUE?

		this.blurringPlane = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), blurMaterial);
		this.blurringPlane.name = "Blurring Plane";
		this.blurringPlane.frustumCulled = false;
		this.blurringPlane.renderOrder = 0;
		(this.blurringPlane.material as THREE.Material).depthWrite = false;
		this.scene.add(this.blurringPlane);
	}

	/** INTERNAL: This function returns the surface area of the input object's geometry as a float
	 * @param {Object3D} camera Standard Rendering Camera
	 * @param {Boolean} uvs Whether to calculate the world surface area, or the used UV surface area. */
	_findSurfaceArea(object: THREE.Object3D, from_uvs: boolean, uvs_override: THREE.BufferAttribute | null = null): number {
		object.updateWorldMatrix(true, false);
		let sum = 0; let tris = [] as number[];
		if (object["geometry"].index) {
			tris = object["geometry"].index.array;
		} else {
			for (let t = 0; t < object["geometry"].attributes.position.count; t++) {
				tris.push(t);
			}
		}

		const vertices = object["geometry"].getAttribute("position");
		const v1 = new THREE.Vector3(0, 0, 0),
			v2 = new THREE.Vector3(0, 0, 0),
			v3 = new THREE.Vector3(0, 0, 0);

		const uvs = uvs_override ?? object["geometry"].getAttribute("uv");
		const uv1 = new THREE.Vector2(0, 0),
			uv2 = new THREE.Vector2(0, 0),
			uv3 = new THREE.Vector2(0, 0);

		for (let t = 0; t < tris.length; t += 3) {
			if (!from_uvs) {
				v1.fromBufferAttribute(vertices, tris[t + 0]);
				v2.fromBufferAttribute(vertices, tris[t + 1]);
				v3.fromBufferAttribute(vertices, tris[t + 2]);
				object.localToWorld(v1); // Multiply by matrixWorld for true size
				object.localToWorld(v2);
				object.localToWorld(v3);
			} else {
				uv1.fromBufferAttribute(uvs, tris[t + 0]); v1.set(uv1.x, uv1.y, 0);
				uv2.fromBufferAttribute(uvs, tris[t + 1]); v2.set(uv2.x, uv2.y, 0);
				uv3.fromBufferAttribute(uvs, tris[t + 2]); v3.set(uv3.x, uv3.y, 0);
			}

			// Calculate the area of this triangle
			v3.sub(v2); v1.sub(v2);
			sum += v3.cross(v1).length() * 0.5;
		}

		return sum;
	}

	/** INTERNAL Creates a new material by injecting new code into an existing material's shader */
	_createModifiedMaterial(
		originalMaterial: THREE.Material,
		uniforms: {[uniform: string]: THREE.IUniform},
		vertexStart: string, vertexPreBody: string, vertexEnd: string,
		fragmentStart: string, fragmentPreBody: string, fragmentEnd: string,
		extensions?: {[extension: string]: boolean},
		skip?: boolean,
		onBeforeCompile_ender?: (shader: THREE.WebGLProgramParametersWithUniforms)=>void // v-added
	): THREE.Material {
		/** @type {THREE.Material} */
		const modifiedMaterial = originalMaterial.clone();

		modifiedMaterial["uniforms"] = uniforms as any;
		if (extensions) {(modifiedMaterial as THREE.Material)["extensions"] = extensions;}

		modifiedMaterial.onBeforeCompile = shader=>{
			const vertexBodyStart = shader.vertexShader.indexOf("void main() {");
			const fragmentBodyStart = shader.fragmentShader.indexOf("void main() {");

			if (!skip) {
				shader.vertexShader = vertexStart + "\n" + shader.vertexShader.slice(0, vertexBodyStart) +
					vertexPreBody + shader.vertexShader.slice(vertexBodyStart - 1, - 1) + vertexEnd;
				shader.fragmentShader = fragmentStart + "\n" + shader.fragmentShader.slice(0, fragmentBodyStart) +
					fragmentPreBody + shader.fragmentShader.slice(fragmentBodyStart - 1, - 1) + fragmentEnd;
			}
			Object.assign(shader.uniforms, uniforms);

			modifiedMaterial["uniforms"] = shader.uniforms as any;
			modifiedMaterial.userData.shader = shader;

			onBeforeCompile_ender?.(shader); // v-added
		};
		return modifiedMaterial;
	}
}